import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api, ApiClientError } from "@repo/api-client";
import type { User, UserProfile } from "@repo/shared-types";

interface AuthState {
  user: Pick<User, "id" | "email" | "role"> | null;
  profile: UserProfile | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  switchPlatform: (platform: "waytomoksha" | "mokshapass") => Promise<void>;
  currentPlatform: "waytomoksha" | "mokshapass";
  hasPlatformAccess: (platform: "waytomoksha" | "mokshapass") => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthState["user"]>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPlatform, setCurrentPlatform] = useState<"waytomoksha" | "mokshapass">("waytomoksha");
  const didBootstrapSessionRef = useRef(false);

  const waitForApiReady = useCallback(async () => {
    const maxAttempts = 5;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await api.health();
        return;
      } catch (error) {
        const isRetryable =
          error instanceof ApiClientError && (error.status === 502 || error.status === 503 || error.status === 504);
        if (!isRetryable || attempt === maxAttempts - 1) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
      }
    }
  }, []);

  const hydrateProfile = useCallback(async () => {
    const [p, membership] = await Promise.all([
      api.getProfile(),
      api.getMembershipTier(),
    ]);
    const merged: UserProfile = {
      ...p,
      tier: membership.tier,
      membership_expires_at: membership.expires_at,
      platform_access: membership.access,
      preferences: p.preferences,
    };
    setProfile(merged);
    const lastPlatform = merged.preferences?.last_platform;
    if (lastPlatform === "waytomoksha" || lastPlatform === "mokshapass") {
      setCurrentPlatform(lastPlatform);
    }
  }, []);

  // Restore session from the refresh cookie on mount.
  useEffect(() => {
    if (didBootstrapSessionRef.current) return;
    didBootstrapSessionRef.current = true;

    let isActive = true;

    const bootstrapSession = async () => {
      try {
        await waitForApiReady();
        const res = await api.refreshToken();
        if (!isActive) return;

        setToken(res.access_token);
        setUser(res.user);
        api.setToken(res.access_token);
        await hydrateProfile();
      } catch {
        if (!isActive) return;
        setToken(null);
        setUser(null);
        setProfile(null);
        api.setToken(null);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    bootstrapSession();

    return () => {
      isActive = false;
      didBootstrapSessionRef.current = false;
    };
  }, [hydrateProfile, waitForApiReady]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login({ email, password });
    setToken(res.access_token);
    setUser(res.user);
    api.setToken(res.access_token);
    await hydrateProfile();
  }, [hydrateProfile]);

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    const res = await api.signUp({ email, password, name });
    setToken(res.access_token);
    setUser(res.user);
    api.setToken(res.access_token);
    await hydrateProfile();
  }, [hydrateProfile]);

  const logout = useCallback(async () => {
    await api.logout().catch(() => {});
    setToken(null);
    setUser(null);
    setProfile(null);
    api.setToken(null);
  }, []);

  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    const updated = await api.updateProfile(updates);
    setProfile((prev) => ({
      ...(prev ?? updated),
      ...updated,
    }));
  }, []);

  const switchPlatform = useCallback(async (platform: "waytomoksha" | "mokshapass") => {
    setCurrentPlatform(platform);
    await updateProfile({
      preferences: {
        ...(profile?.preferences ?? {}),
        last_platform: platform,
      },
    });
  }, [profile?.preferences, updateProfile]);

  const hasPlatformAccess = useCallback((platform: "waytomoksha" | "mokshapass") => {
    return Boolean(profile?.platform_access?.[platform]);
  }, [profile]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        token,
        loading,
        login,
        signUp,
        logout,
        updateProfile,
        switchPlatform,
        currentPlatform,
        hasPlatformAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
